// frontend(root)/src/components/comunity/ActivityFeed.js
import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import useActivityFeed from "../../hooks/useActivityFeed";
import { activityApi } from "../../services/apiService";
import "./ActivityFeed.css";

const ActivityFeed = ({ userId = null }) => {
  const { isLoggedIn, user } = useAuth();
  const [expandedNotes, setExpandedNotes] = useState({});
  
  // If userId is provided, fetch that user's activities, otherwise use the activity feed
  const { 
    activities, 
    loading, 
    error, 
    filter, 
    hasMore, 
    loadMore, 
    changeFilter, 
    refresh 
  } = useActivityFeed(userId ? null : 'all');

  const observerRef = useRef();
  const lastActivityRef = useRef();

  // Set up infinite scroll with Intersection Observer
  useEffect(() => {
    if (loading) return;
    
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMore();
      }
    });
    
    if (lastActivityRef.current) {
      observerRef.current.observe(lastActivityRef.current);
    }
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, hasMore, loadMore]);

  // Format the timestamp for display
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
      return "Just now";
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? "day" : "days"} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Get activity text based on the type
  const getActivityText = (activity) => {
    const { type, itemType } = activity;
    const data = activity.data || {}; // Default to empty object if data is undefined
    const title = activity.itemId.title_english || activity.itemId.title || activity.itemId.name;
    
    // Create base description based on type
    let baseText = "";
    switch (type) {
      case "added":
        if (data.status) {
          switch (data.status) {
            case "planning":
              baseText = `added ${title} to their ${itemType === 'manga' ? 'plan to read' : 'plan to watch'} list`;
              break;
            case "watching":
              baseText = `started watching ${title}`;
              break;
            case "reading":
              baseText = `started reading ${title}`;
              break;
            case "completed":
              baseText = `added ${title} to their completed list`;
              break;
            case "onHold":
              baseText = `added ${title} to their on-hold list`;
              break;
            case "dropped":
              baseText = `added ${title} to their dropped list`;
              break;
            default:
              baseText = `added ${title} to their ${data.status} list`;
          }
        } else {
          baseText = `added ${title} to their list`;
        }
        break;
      case "updated":
        if (data.status) {
          switch (data.status) {
            case "planning":
              baseText = `moved ${title} to their ${itemType === 'manga' ? 'plan to read' : 'plan to watch'} list`;
              break;
            case "watching":
              baseText = `started watching ${title}`;
              break;
            case "reading":
              baseText = `started reading ${title}`;
              break;
            case "completed":
              baseText = `marked ${title} as completed`;
              break;
            case "onHold":
              baseText = `put ${title} on hold`;
              break;
            case "dropped":
              baseText = `dropped ${title}`;
              break;
            default:
              baseText = `updated ${title} to ${data.status}`;
          }
        } else {
          baseText = `updated ${title}`;
        }
        break;
      case "watched":
        baseText = `watched ${data.progress || "an episode"} of ${title}`;
        break;
      case "read":
        baseText = `read ${data.progress || "a chapter"} of ${title}`;
        break;
      case "rated":
        baseText = `rated ${title} ${data.rating}/10`;
        break;
      case "completed":
        baseText = `completed ${title}`;
        break;
      case "note":
        baseText = `added a note to ${title}`;
        break;
      default:
        baseText = `updated ${title}`;
    }
    
    return baseText;
  };

  // Toggle expanded note state
  const toggleNoteExpand = (activityId) => {
    setExpandedNotes(prev => ({
      ...prev,
      [activityId]: !prev[activityId]
    }));
  };

  // Get image URL from item
  const getItemImage = (item) => {
    if (!item) return null;
    
    if (item.images?.jpg?.image_url) {
      return item.images.jpg.image_url;
    } else if (item.images?.jpg?.small_image_url) {
      return item.images.jpg.small_image_url;
    } else if (item.images?.webp?.image_url) {
      return item.images.webp.image_url;
    }
    
    return null;
  };

  // Function to group related activities from the same user in a short time window
  const groupRelatedActivities = (activities, timeWindowMs = 10000) => {
    if (!activities || activities.length === 0) return [];
    
    console.log("Time window is set to", timeWindowMs, "milliseconds");
    
    const groupedActivities = [];
    let currentGroup = [activities[0]];
    
    for (let i = 1; i < activities.length; i++) {
        const currentActivity = activities[i];
        const firstActivityInGroup = currentGroup[0];
        
        // Get timestamps and format them for easy reading
        const currentTime = new Date(currentActivity.createdAt).getTime();
        const firstActivityTime = new Date(firstActivityInGroup.createdAt).getTime();
        
        // Calculate the ABSOLUTE time difference (using Math.abs to handle any ordering issues)
        const timeDiff = Math.abs(currentTime - firstActivityTime);
        
        console.log("Activity time difference:", timeDiff, "ms");
        console.log("Current activity:", new Date(currentActivity.createdAt).toLocaleString());
        console.log("First activity in group:", new Date(firstActivityInGroup.createdAt).toLocaleString());
        
        // Check conditions
        const sameUser = currentActivity.userId.id === firstActivityInGroup.userId.id;
        const sameItem = currentActivity.itemId.id === firstActivityInGroup.itemId.id;
        const closeInTime = timeDiff <= timeWindowMs;
        
        console.log("Same user:", sameUser);
        console.log("Same item:", sameItem);
        console.log("Close in time:", closeInTime);
        
        if (sameUser && sameItem && closeInTime) {
            console.log("Grouping activities");
            currentGroup.push(currentActivity);
        } else {
            console.log("Starting new group");
            groupedActivities.push(currentGroup);
            currentGroup = [currentActivity];
        }
    }
    
    // Add the last group
    if (currentGroup.length > 0) {
        groupedActivities.push(currentGroup);
    }
    
    return groupedActivities;
};

  // Get activity text for grouped activities
  const getGroupedActivityText = (activityGroup) => {
    if (activityGroup.length === 1) {
      // Just one activity, use the regular text function
      return getActivityText(activityGroup[0]);
    }
    
    const title = activityGroup[0].itemId.title_english || activityGroup[0].itemId.title || activityGroup[0].itemId.name;
    
    // Check for common combinations
    const types = activityGroup.map(a => a.type);
    
    // Episode update + completion
    if (types.includes('watched') && types.includes('completed')) {
      const watchedActivity = activityGroup.find(a => a.type === 'watched');
      const progress = watchedActivity.data?.progress || "the final episode";
      return `watched ${progress} and completed ${title}`;
    }
    
    // Completion + rating
    if (types.includes('completed') && types.includes('rated')) {
      const ratedActivity = activityGroup.find(a => a.type === 'rated');
      const rating = ratedActivity.data?.rating || "?";
      return `completed ${title} and rated it ${rating}/10`;
    }
    
    // Status change + note
    if (types.includes('updated') && types.includes('note')) {
      const updatedActivity = activityGroup.find(a => a.type === 'updated');
      const status = updatedActivity.data?.status;
      
      if (status) {
        let statusText = "";
        switch (status) {
          case 'onHold':
            statusText = "put on hold";
            break;
          case 'dropped':
            statusText = "dropped";
            break;
          default:
            statusText = `moved to their ${status} list`;
        }
        
        return `${statusText} ${title} and added a note`;
      } else {
        return `updated ${title} and added a note`;
      }
    }
    
    // Episode update + rating
    if (types.includes('watched') && types.includes('rated')) {
      const watchedActivity = activityGroup.find(a => a.type === 'watched');
      const ratedActivity = activityGroup.find(a => a.type === 'rated');
      const progress = watchedActivity.data?.progress || "an episode";
      const rating = ratedActivity.data?.rating || "?";
      return `watched ${progress} of ${title} and rated it ${rating}/10`;
    }
    
    // Chapter update + rating (for manga)
    if (types.includes('read') && types.includes('rated')) {
      const readActivity = activityGroup.find(a => a.type === 'read');
      const ratedActivity = activityGroup.find(a => a.type === 'rated');
      const progress = readActivity.data?.progress || "a chapter";
      const rating = ratedActivity.data?.rating || "?";
      return `read ${progress} of ${title} and rated it ${rating}/10`;
    }
    
    // Default case: just list the actions
    const activityVerbs = activityGroup.map(activity => {
      switch (activity.type) {
        case 'added': return 'added';
        case 'updated': return 'updated';
        case 'watched': return 'watched an episode of';
        case 'read': return 'read a chapter of';
        case 'rated': return 'rated';
        case 'completed': return 'completed';
        case 'note': return 'added a note to';
        default: return 'updated';
      }
    });
    
    return `${activityVerbs.join(', ')} ${title}`;
  };

  // Handle deleting a group of activities
  const handleDeleteGroup = async (group) => {
    if (!window.confirm("Delete this activity?")) return;
    
    try {
      // Delete each activity in the group
      for (const activity of group) {
        // Use the API service if available
        if (activityApi && activityApi.deleteActivity) {
          await activityApi.deleteActivity(activity.id);
        } else {
          // Fallback to direct fetch
          await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000'}/api/activities/${activity.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
        }
      }
      
      // Refresh the feed
      refresh();
    } catch (err) {
      console.error("Error deleting activities:", err);
      alert("Failed to delete activity. Please try again.");
    }
  };

  if (loading && activities.length === 0) {
    return (
      <div className="activity-feed-loading">
        <div className="spinner"></div>
        <p>Loading activity feed...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="activity-feed-error">
        <p>{error}</p>
        <button onClick={refresh}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="activity-feed-container">
      {!userId && (
        <div className="activity-feed-header">
          <h2>Activity Feed</h2>
          <div className="feed-filters">
            <button 
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => changeFilter('all')}
            >
              All Activity
            </button>
            <button 
              className={`filter-btn ${filter === 'friends' ? 'active' : ''}`}
              onClick={() => changeFilter('friends')}
            >
              Friends
            </button>
            <button 
              className={`filter-btn ${filter === 'you' ? 'active' : ''}`}
              onClick={() => changeFilter('you')}
            >
              Your Activity
            </button>
          </div>
        </div>
      )}

      {activities.length === 0 ? (
        <div className="no-activities">
          <p>
            {userId 
              ? "This user has no recent activity." 
              : "No activities to display. Follow more users to see their updates!"}
          </p>
        </div>
      ) : (
        <div className="activities-list">
          {groupRelatedActivities(activities).map((group, groupIndex) => {
            // Use the first activity for user info and timestamps
            const primaryActivity = group[0];
            const isLastGroup = groupIndex === groupRelatedActivities(activities).length - 1;
            
            return (
              <div 
                key={primaryActivity.id} 
                className="activity-card"
                ref={isLastGroup ? lastActivityRef : null}
              >
                <div className="activity-user-avatar">
                  <img 
                    src={primaryActivity.userId.profilePicture || "https://via.placeholder.com/40"} 
                    alt={primaryActivity.userId.username} 
                  />
                </div>
                <div className="activity-content">
                  <div className="activity-header">
                    <a href={`/users/${primaryActivity.userId.id}`} className="activity-username">
                      {primaryActivity.userId.username}
                    </a>
                    <span className="activity-text">{getGroupedActivityText(group)}</span>
                  </div>
                  
                  <a href={`/${primaryActivity.itemType}/${primaryActivity.itemId.id}`} className="activity-item">
                    <div className="activity-item-image">
                      <img 
                        src={getItemImage(primaryActivity.itemId) || "https://via.placeholder.com/50x70"} 
                        alt={primaryActivity.itemId.title_english || primaryActivity.itemId.title || primaryActivity.itemId.name} 
                      />
                    </div>
                    <div className="activity-item-info">
                      <p className="activity-item-title">
                        {primaryActivity.itemId.title_english || primaryActivity.itemId.title || primaryActivity.itemId.name}
                      </p>
                      <span className="activity-item-type">{primaryActivity.itemType}</span>
                    </div>
                  </a>
                  
                  {/* Show notes if any activity in the group has a note */}
                  {group.some(a => a.type === "note") && (
                    group.filter(a => a.type === "note").map(noteActivity => (
                      <div key={noteActivity.id} className="activity-note">
                        <p>
                          {noteActivity.data?.note?.length > 100 && !expandedNotes[noteActivity.id]
                            ? `"${noteActivity.data.note.substring(0, 100)}..."` 
                            : `"${noteActivity.data.note}"`}
                        </p>
                        {noteActivity.data?.note?.length > 100 && (
                          <button 
                            className="note-toggle" 
                            onClick={() => toggleNoteExpand(noteActivity.id)}
                          >
                            {expandedNotes[noteActivity.id] ? "Show less" : "Show more"}
                          </button>
                        )}
                      </div>
                    ))
                  )}
                  
                  <div className="activity-footer">
                    <span className="activity-timestamp">{formatTimestamp(primaryActivity.createdAt)}</span>
                    {isLoggedIn && (
                      <div className="activity-actions">
                        <button className="action-btn like-btn">Like</button>
                        <button className="action-btn comment-btn">Comment</button>
                        {user && user.id === primaryActivity.userId.id && (
                          <button 
                            className="action-btn delete-btn"
                            onClick={() => handleDeleteGroup(group)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          
          {loading && activities.length > 0 && (
            <div className="loading-more">
              <div className="spinner small"></div>
              <p>Loading more...</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;